import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsivePie } from '@nivo/pie';
import { Fetch } from '../../utils/Utils.js';
import './NivoChartCard.css';
import './ReproductionRecentResults.css';

// Outcomes are genuinely a status (good/warning/serious/critical), not an
// arbitrary identity — pregnant/calved_alive share the "good" tone (an
// in-progress positive vs. a resolved positive), distinguished by icon+label
// rather than a manufactured extra hue. Ink picked per measured contrast
// against each status fill — good and serious both read best with dark ink
// despite being the "darker-looking" swatches at a glance.
const STATUS_COLOR = {
  pregnant:           '#0ca30c',
  calved_alive:       '#0ca30c',
  returned_to_estrus: '#fab219',
  calved_stillborn:   '#ec835a',
  aborted:            '#d03b3b',
};
const STATUS_INK = {
  pregnant: 'dark', calved_alive: 'dark', returned_to_estrus: 'dark',
  calved_stillborn: 'dark', aborted: 'light',
};
const STATUS_TONE = {
  pregnant: 'good', calved_alive: 'good', returned_to_estrus: 'warning',
  calved_stillborn: 'serious', aborted: 'critical',
};
// Hand-drawn icon set (24x24, stroke-based) rather than unicode glyphs, which
// render inconsistently in weight/alignment across platforms.
const ICON = {
  pregnant:           <svg viewBox="0 0 24 24" width="13" height="13"><circle cx="12" cy="12" r="7" fill="currentColor" /></svg>,
  calved_alive:       <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5 5L20 6" /></svg>,
  returned_to_estrus: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M4 12h16" /></svg>,
  calved_stillborn:   <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M12 4v10" /><circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" /></svg>,
  aborted:            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M5 5l14 14M19 5L5 19" /></svg>,
};
const OUTCOME_ORDER = ['pregnant', 'calved_alive', 'returned_to_estrus', 'calved_stillborn', 'aborted'];

const CenterLabel = (totalCount, animalsWord) => ({ centerX, centerY }) => (
  <g>
    <text x={centerX} y={centerY - 6} textAnchor="middle" dominantBaseline="middle" className="repro-hero-value">
      {totalCount}
    </text>
    <text x={centerX} y={centerY + 15} textAnchor="middle" dominantBaseline="middle" className="repro-hero-label">
      {animalsWord}
    </text>
  </g>
);

const ReproductionRecentResults = () => {
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState([]);

  useEffect(() => {
    Fetch('/api/reproduction/results', 'GET', null, (data) => setResults(data || []));
  }, []);

  const now = new Date();
  const currentMonth = now.toISOString().split('T')[0].substring(0, 7);
  const monthLabel = now.toLocaleDateString(i18n.language || 'en-US', { month: 'long', year: 'numeric' });
  const animalsLabel = (n) => (n === 1 ? t('animal_count_singular') : t('animals_count_short'));

  const counts = {};
  let damDiedCount = 0;
  results.forEach(r => {
    if ((r.start_date || '').substring(0, 7) !== currentMonth) return;
    // A dam dying isn't a protocol/process outcome — pulled out of the pie
    // entirely (not just another slice) and surfaced as a note instead.
    if (r.outcome === 'dam_died') {
      damDiedCount += 1;
      return;
    }
    counts[r.outcome] = (counts[r.outcome] || 0) + 1;
  });

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const slices = OUTCOME_ORDER
    .filter(outcome => counts[outcome] > 0)
    .map(outcome => ({
      outcome,
      count: counts[outcome],
      percent: Math.round((counts[outcome] / total) * 100),
    }));

  const pieData = slices.map(s => ({ id: s.outcome, label: t(s.outcome), value: s.count, color: STATUS_COLOR[s.outcome] }));

  return (
    <div className="Chart NivoCard ReproductionRecentResults">
      <h3>{t('reproduction_results_chart_title')}</h3>
      <p className="nivo-card-subtitle">{t('for_month', { month: monthLabel })}</p>

      {total === 0 ? (
        <p className="nivo-card-empty">{t('chart_summary_no_repro_month')}</p>
      ) : (
        <div className="repro-results-body">
          <div className="repro-donut-wrap">
            <ResponsivePie
              data={pieData}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.62}
              padAngle={1.6}
              cornerRadius={4}
              activeOuterRadiusOffset={6}
              colors={{ datum: 'data.color' }}
              borderWidth={0}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={26}
              arcLabelsTextColor={d => (STATUS_INK[d.id] === 'light' ? '#ffffff' : '#1a1714')}
              arcLabel={d => `${Math.round((d.value / total) * 100)}%`}
              motionConfig="gentle"
              layers={['arcs', 'arcLabels', CenterLabel(total, animalsLabel(total))]}
              tooltip={({ datum }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: STATUS_COLOR[datum.id] }} />
                  <strong>{t(datum.id)}</strong>: {datum.value} ({Math.round((datum.value / total) * 100)}%)
                </div>
              )}
            />
          </div>

          <ul className="repro-legend">
            {slices.map(s => (
              <li
                key={s.outcome}
                className="repro-legend-row"
                style={{ '--row-accent': STATUS_COLOR[s.outcome] }}
              >
                <span className={`repro-legend-icon repro-icon-${STATUS_TONE[s.outcome]}`}>{ICON[s.outcome]}</span>
                <span className="repro-legend-text">
                  <span className="repro-legend-headline">
                    <strong className="repro-legend-percent">{s.percent}%</strong>
                    {' '}{t(`chart_outcome_label_${s.outcome}`)}
                  </span>
                  <span className="repro-legend-count">{s.count} {animalsLabel(s.count)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {damDiedCount > 0 && (
        <p className="repro-dam-died-note">
          {t(damDiedCount === 1 ? 'chart_dam_died_note_singular' : 'chart_dam_died_note_plural', { count: damDiedCount })}
        </p>
      )}
    </div>
  );
};

export default ReproductionRecentResults;
